import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import mabarService, { ParticipationEvent } from '@/services/mabarService';
import padelService from '@/services/padelService';
import badmintonService from '@/services/badmintonService';
import showToast from '@/components/ui/Toast';
import { downloadOrShareBlob, shareBlob } from '@/utils/downloadBlob';
import { formatDateDDMMYYYY } from '@/utils/dateUtils';
import { RecapCardRenderer, RecapTemplateVariant } from '@/components/ui/recap/RecapCardRenderer';
import { KlasemenStandingItem } from '@/components/ui/recap/KlasemenCard';
import { StatistikExportActions } from '@/components/ui/StatistikExportActions';
import { getClientProfile, DataProfile } from '@/services/clientProfile';

export interface SportCareerStat {
  goals: number;
  assists: number;
  wins: number;
  totalGames: number;
  point?: number;
  pointLoss?: number;
  isRacket?: boolean;
  cleansheets?: number;
  keyPasses?: number;
  saves?: number;
  clearances?: number;
  fouls?: number;
  motm?: number;
  freestyle?: number;
  winstreak?: number;
}

interface RaporEventCardProps {
  userDisplayName?: string;
  userAvatar?: string;
  refreshKey?: number;
  eventSlug?: string;
  buttonOnly?: boolean;
}

export interface MetricCatalogItem {
  key: string;
  label: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  getValue: (stats: SportCareerStat, winRate: number) => number | string;
}

export const INVASION_METRIC_CATALOG: MetricCatalogItem[] = [
  { key: 'total_match', label: 'Total Match', icon: 'ph:flag-banner-fill', iconBg: 'bg-[#FEF6E6]', iconColor: 'text-[#D97706]', getValue: (s) => s.totalGames },
  { key: 'winrate', label: 'Winrate', icon: 'ph:trophy-fill', iconBg: 'bg-[#EBFBEE]', iconColor: 'text-[#16A34A]', getValue: (_s, wr) => `${wr}%` },
  { key: 'motm', label: 'MOTM', icon: 'ph:star-fill', iconBg: 'bg-amber-50', iconColor: 'text-amber-500', getValue: (s) => s.motm ?? 0 },
  { key: 'gol', label: 'Gol', icon: 'ph:soccer-ball-fill', iconBg: 'bg-[#FFF4EE]', iconColor: 'text-[#FC480D]', getValue: (s) => s.goals },
  { key: 'assist', label: 'Assist', icon: 'ph:sneaker-move-fill', iconBg: 'bg-[#FFF4EE]', iconColor: 'text-[#FC480D]', getValue: (s) => s.assists },
  { key: 'key_pass', label: 'Key Pass', icon: 'ph:target-fill', iconBg: 'bg-[#FFF4EE]', iconColor: 'text-[#FC480D]', getValue: (s) => s.keyPasses ?? 0 },
  { key: 'cleansheet', label: 'Cleansheet', icon: 'ph:shield-fill', iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', getValue: (s) => s.cleansheets ?? 0 },
  { key: 'save', label: 'Save', icon: 'ph:hand-palm-fill', iconBg: 'bg-orange-50', iconColor: 'text-orange-600', getValue: (s) => s.saves ?? 0 },
  { key: 'clearance', label: 'Clearance', icon: 'ph:shield-check-fill', iconBg: 'bg-indigo-50', iconColor: 'text-indigo-600', getValue: (s) => s.clearances ?? 0 },
  { key: 'foul', label: 'Foul', icon: 'ph:warning-fill', iconBg: 'bg-red-50', iconColor: 'text-red-500', getValue: (s) => s.fouls ?? 0 },
  { key: 'freestyle', label: 'Freestyle', icon: 'ph:sparkle-fill', iconBg: 'bg-purple-50', iconColor: 'text-purple-600', getValue: (s) => s.freestyle ?? 0 },
];

export const RACKET_METRIC_CATALOG: MetricCatalogItem[] = [
  { key: 'poin', label: 'Point', icon: 'ph:tennis-ball-fill', iconBg: 'bg-[#FFF4EE]', iconColor: 'text-[#FC480D]', getValue: (s) => s.point ?? 0 },
  { key: 'point_loss', label: 'Point Loss', icon: 'ph:shield-warning-fill', iconBg: 'bg-[#FFF4EE]', iconColor: 'text-[#FC480D]', getValue: (s) => s.pointLoss ?? 0 },
  { key: 'total_match', label: 'Total Match', icon: 'ph:flag-banner-fill', iconBg: 'bg-[#FEF6E6]', iconColor: 'text-[#D97706]', getValue: (s) => s.totalGames },
  { key: 'winrate', label: 'Win Rate', icon: 'ph:trophy-fill', iconBg: 'bg-[#EBFBEE]', iconColor: 'text-[#16A34A]', getValue: (_s, wr) => `${wr}%` },
  { key: 'total_menang', label: 'Total Menang', icon: 'ph:trophy-fill', iconBg: 'bg-amber-50', iconColor: 'text-amber-600', getValue: (s) => s.wins },
  { key: 'total_kalah', label: 'Total Kalah', icon: 'ph:x-circle-fill', iconBg: 'bg-red-50', iconColor: 'text-red-500', getValue: (s) => Math.max(0, s.totalGames - s.wins) },
  { key: 'winstreak', label: 'Best Winstreak', icon: 'ph:flame-fill', iconBg: 'bg-orange-50', iconColor: 'text-orange-500', getValue: (s) => s.winstreak ?? 0 },
];

export function findMetricFromCatalog(catalog: MetricCatalogItem[], key: string): MetricCatalogItem {
  const direct = catalog.find((m) => m.key === key);
  if (direct) return direct;

  const keyMap: Record<string, string> = {
    clearances: 'clearance',
    cleansheets: 'cleansheet',
    clean_sheet: 'cleansheet',
    key_passes: 'key_pass',
    saves: 'save',
    fouls: 'foul',
    win_rate: 'winrate',
    total_games: 'total_match',
    point: 'poin',
    goals: 'gol',
    assists: 'assist',
  };

  const mappedKey = keyMap[key];
  if (mappedKey) {
    const mappedItem = catalog.find((m) => m.key === mappedKey);
    if (mappedItem) return mappedItem;
  }

  return catalog[0];
}

export function resolveFourStats(
  selectedKeys?: string[],
  stats?: any,
  isRacket?: boolean
) {
  const catalog = isRacket ? RACKET_METRIC_CATALOG : INVASION_METRIC_CATALOG;
  const defaultKeys = isRacket
    ? ['poin', 'point_loss', 'total_match', 'winrate']
    : ['gol', 'assist', 'key_pass', 'clearance'];

  const keys = selectedKeys && selectedKeys.length === 4 ? selectedKeys : defaultKeys;
  const st: SportCareerStat = {
    goals: stats?.goals || 0,
    assists: stats?.assists || 0,
    totalGames: stats?.matches || stats?.totalGames || 0,
    wins: stats?.wins !== undefined ? stats.wins : 0,
    point: stats?.point || 0,
    pointLoss: stats?.point_loss || stats?.pointLoss || 0,
    motm: stats?.motm || 0,
    cleansheets: stats?.cleansheets || stats?.clean_sheet || stats?.cleansheet || 0,
    keyPasses: stats?.keyPasses || stats?.key_passes || stats?.key_pass || 0,
    saves: stats?.saves || stats?.save || 0,
    clearances: stats?.clearances || stats?.clearance || 0,
    fouls: stats?.fouls || stats?.foul || 0,
    freestyle: stats?.freestyle || 0,
    winstreak: stats?.winstreak || 0,
  };

  const calculatedWinRate = stats?.win_rate !== undefined
    ? stats.win_rate
    : (st.totalGames > 0 ? Math.round((st.wins / st.totalGames) * 100) : 0);

  return keys.map((key) => {
    const itemDef = findMetricFromCatalog(catalog, key);
    return {
      key: itemDef.key,
      label: itemDef.label,
      icon: itemDef.icon,
      iconBg: itemDef.iconBg,
      iconColor: itemDef.iconColor,
      value: itemDef.getValue(st, calculatedWinRate),
    };
  });
}

const INVASION_TEMPLATES = [
  {
    id: 'sorotan',
    title: 'Sorotan',
    desc: 'Angka bertumpuk, foto dominan',
    icon: 'ph:list-numbers-bold',
  },
  {
    id: 'kartu_pemain',
    title: 'Kartu Pemain',
    desc: 'Kartu statistik + identitas',
    icon: 'ph:identification-card-bold',
  },
  {
    id: 'rapor_event',
    title: 'Rapor Event',
    desc: 'Judul event & rincian lengkap',
    icon: 'ph:ticket-bold',
  },
] as const;

const RACKET_TEMPLATES = [
  {
    id: 'rapor_event_racket',
    title: 'Rapor Event',
    desc: 'Ringkasan Padel & Badminton',
    icon: 'ph:ticket-bold',
  },
  {
    id: 'klasemen',
    title: 'Klasemen',
    desc: 'Tabel & posisi podium akhir',
    icon: 'ph:trophy-bold',
  },
] as const;

const isSupportedCategory = (ev: ParticipationEvent): boolean => {
  if (!ev) return false;
  const catSlug = (
    ev.sport_category?.slug ||
    ev.sport_category?.name ||
    (typeof ev.sport_category === 'string' ? ev.sport_category : '')
  ).toLowerCase().replace(/[^a-z0-9]/g, '');

  return (
    catSlug.includes('futsal') ||
    catSlug.includes('soccer') ||
    catSlug.includes('mini') ||
    catSlug.includes('sepak') ||
    catSlug.includes('football') ||
    catSlug.includes('padel') ||
    catSlug.includes('badminton') ||
    catSlug.includes('racket') ||
    catSlug.includes('raket')
  );
};

const getEventTimestamp = (ev: ParticipationEvent): number => {
  if (!ev || !ev.event_date) return 0;
  const dateStr = ev.event_date.split('T')[0];
  const timeStr = ev.start_time ? ev.start_time.trim() : '00:00';
  const fullStr = `${dateStr}T${timeStr.length === 5 ? timeStr + ':00' : timeStr}`;
  const parsed = new Date(fullStr).getTime();
  if (!isNaN(parsed)) return parsed;
  return new Date(dateStr).getTime() || 0;
};

const pickBestRaporEvent = (events: ParticipationEvent[]): ParticipationEvent | null => {
  if (!events || events.length === 0) return null;
  const now = Date.now();

  // Events that have started or occurred up to now (+ 12 hour buffer for ongoing events)
  const pastOrCurrent = events.filter((ev) => getEventTimestamp(ev) <= now + 12 * 3600 * 1000);

  if (pastOrCurrent.length > 0) {
    pastOrCurrent.sort((a, b) => getEventTimestamp(b) - getEventTimestamp(a));
    return pastOrCurrent[0];
  }

  // Fallback: pick closest upcoming event if no past event exists
  const upcoming = [...events].sort((a, b) => getEventTimestamp(a) - getEventTimestamp(b));
  return upcoming[0];
};

export const RaporEventCard: React.FC<RaporEventCardProps> = ({
  userDisplayName = 'Player',
  userAvatar,
  refreshKey = 0,
  eventSlug,
  buttonOnly = false,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentEvent, setCurrentEvent] = useState<ParticipationEvent | null>(null);
  const [eventStats, setEventStats] = useState<SportCareerStat>({
    goals: 0,
    assists: 0,
    wins: 0,
    totalGames: 0,
    point: 0,
    pointLoss: 0,
    isRacket: false,
  });
  const [eventDeltaMMR, setEventDeltaMMR] = useState<number | null>(null);
  const [standings, setStandings] = useState<KlasemenStandingItem[]>([]);
  const [userRank, setUserRank] = useState<number>(1);

  const [isShareOpen, setIsShareOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<RecapTemplateVariant>('sorotan');

  // Customer Profile & Manage Stats Config
  const [profile, setProfile] = useState<DataProfile | null>(null);
  const [exportStatKeys, setExportStatKeys] = useState<string[]>([]);

  // Load Customer Profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getClientProfile();
        if (res?.success && res.data) {
          setProfile(res.data);
        }
      } catch (err) {
        console.error('Failed to load profile for export stat config:', err);
      }
    };
    fetchProfile();
  }, []);

  // Fetch participations (upcoming & history) on mount or use target event if eventSlug is provided
  useEffect(() => {
    const fetchParticipations = async () => {
      try {
        setLoading(true);

        if (eventSlug) {
          const [evDetail, st] = await Promise.all([
            mabarService.getEventBySlug(eventSlug).catch(() => null),
            mabarService.getEventMyStats(eventSlug).catch(() => null),
          ]);

          if (evDetail) {
            const targetEv: ParticipationEvent = {
              slug: evDetail.slug,
              name: evDetail.name,
              description: evDetail.description || '',
              event_date: evDetail.event_date,
              start_time: evDetail.start_time,
              end_time: evDetail.end_time,
              city: evDetail.city || '',
              address: evDetail.address || '',
              lokasi_arena: evDetail.lokasi_arena || '',
              map_link: evDetail.map_link || '',
              sport_category: evDetail.sport_category,
              visibility: evDetail.visibility || 'public',
              type: evDetail.type || 'free',
              status: evDetail.status || '',
              total_slots: evDetail.total_slots || 0,
              filled_slots: evDetail.filled_slots || 0,
              available_slots: evDetail.available_slots || 0,
              slot_percentage: evDetail.slot_percentage || 0,
              roles_availability: evDetail.roles_availability || [],
              participants: evDetail.participants || [],
              created_at: evDetail.created_at || '',
              is_host: evDetail.is_host || false,
              auto_confirm: (evDetail as any).auto_confirm ?? false,
            };
            setCurrentEvent(targetEv);
          }

          if (st) {
            setEventStats({
              goals: st.goals || 0,
              assists: st.assists || 0,
              wins: Math.round(((st.win_rate || 0) * (st.matches || 0)) / 100),
              totalGames: st.matches || 0,
              point: st.point || 0,
              pointLoss: st.point_loss || 0,
              isRacket: st.is_racket || false,
              cleansheets: (st as any).clean_sheet ?? (st as any).cleansheet ?? 0,
              keyPasses: (st as any).key_passes ?? (st as any).key_pass ?? 0,
              saves: (st as any).saves ?? (st as any).save ?? 0,
              clearances: (st as any).clearances ?? (st as any).clearance ?? 0,
              fouls: (st as any).fouls ?? (st as any).foul ?? 0,
              motm: (st as any).motm ?? 0,
              freestyle: (st as any).freestyle ?? 0,
              winstreak: (st as any).winstreak ?? 0,
            });
            if (st.has_mmr || st.delta_mmr !== undefined) {
              setEventDeltaMMR(st.delta_mmr ?? 0);
            } else {
              setEventDeltaMMR(null);
            }
          }
          return;
        }

        const [upcomingRes, historyRes] = await Promise.all([
          mabarService.getMyParticipations('upcoming', 1, 50).catch(() => null),
          mabarService.getMyParticipations('history', 1, 50).catch(() => null),
        ]);

        const upcomingEvents = upcomingRes?.data || [];
        const historyEvents = historyRes?.data || [];

        const allEventsMap = new Map<string, ParticipationEvent>();
        [...upcomingEvents, ...historyEvents].forEach((ev) => {
          if (ev && ev.slug) {
            allEventsMap.set(ev.slug, ev);
          }
        });

        const allEvents = Array.from(allEventsMap.values());
        const supportedEvents = allEvents.filter(isSupportedCategory);
        const activeEv = pickBestRaporEvent(supportedEvents) || supportedEvents[0] || allEvents[0] || null;
        setCurrentEvent(activeEv);

        if (activeEv?.slug) {
          try {
            const st = await mabarService.getEventMyStats(activeEv.slug);
            setEventStats({
              goals: st.goals || 0,
              assists: st.assists || 0,
              wins: Math.round(((st.win_rate || 0) * (st.matches || 0)) / 100),
              totalGames: st.matches || 0,
              point: st.point || 0,
              pointLoss: st.point_loss || 0,
              isRacket: st.is_racket || false,
              cleansheets: (st as any).clean_sheet ?? (st as any).cleansheet ?? 0,
              keyPasses: (st as any).key_passes ?? (st as any).key_pass ?? 0,
              saves: (st as any).saves ?? (st as any).save ?? 0,
              clearances: (st as any).clearances ?? (st as any).clearance ?? 0,
              fouls: (st as any).fouls ?? (st as any).foul ?? 0,
              motm: (st as any).motm ?? 0,
              freestyle: (st as any).freestyle ?? 0,
              winstreak: (st as any).winstreak ?? 0,
            });
            if (st.has_mmr || st.delta_mmr !== undefined) {
              setEventDeltaMMR(st.delta_mmr ?? 0);
            } else {
              setEventDeltaMMR(null);
            }
          } catch {
            setEventStats({ goals: 0, assists: 0, wins: 0, totalGames: 0, point: 0, pointLoss: 0, isRacket: false });
            setEventDeltaMMR(null);
          }
        }
      } catch (err) {
        console.error('Failed to load past mabar history:', err);
        setCurrentEvent(null);
      } finally {
        setLoading(false);
      }
    };
    fetchParticipations();
  }, [refreshKey, eventSlug]);

  const isRacket =
    eventStats.isRacket ||
    (currentEvent
      ? ['padel', 'badminton', 'racket', 'raket'].some((s) =>
          (
            currentEvent.sport_category?.slug ||
            currentEvent.sport_category?.name ||
            (typeof currentEvent.sport_category === 'string'
              ? currentEvent.sport_category
              : '')
          )
            .toLowerCase()
            .includes(s)
        )
      : false);

  // Sync export_stat_config for active sport
  useEffect(() => {
    const sportSlug = (
      currentEvent?.sport_category?.slug ||
      currentEvent?.sport_category?.name ||
      ''
    ).toLowerCase();

    const sportKey = isRacket
      ? (sportSlug.includes('badminton') ? 'badminton' : sportSlug.includes('padel') ? 'padel' : 'racket_game')
      : (sportSlug.includes('minisoccer') ? 'minisoccer' : sportSlug.includes('sepak') ? 'sepakbola' : 'futsal');

    const backendExportConfig = profile?.information?.export_stat_config;
    const backendStatConfig = profile?.information?.stat_config;

    const defaultFallback = isRacket
      ? ['poin', 'point_loss', 'total_match', 'winrate']
      : ['gol', 'assist', 'key_pass', 'clearance'];

    const savedKeys =
      backendExportConfig?.[sportKey] ||
      backendExportConfig?.[isRacket ? 'racket_game' : 'invasion_game'] ||
      backendStatConfig?.[sportKey] ||
      JSON.parse(localStorage.getItem(`sporto_export_stat_config_${sportKey}`) || 'null') ||
      defaultFallback;

    if (Array.isArray(savedKeys) && savedKeys.length === 4) {
      setExportStatKeys(savedKeys);
    } else {
      setExportStatKeys(defaultFallback);
    }
  }, [profile, currentEvent, isRacket]);

  // Set template default based on game category (Invasion vs Racket)
  useEffect(() => {
    if (isRacket) {
      setSelectedTemplate('rapor_event_racket');
    } else {
      setSelectedTemplate('sorotan');
    }
  }, [isRacket]);

  // Fetch standings for Racket games (Padel & Badminton)
  useEffect(() => {
    if (!currentEvent?.slug || !isRacket) return;
    const catSlug = (
      currentEvent.sport_category?.slug ||
      currentEvent.sport_category?.name ||
      (typeof currentEvent.sport_category === 'string' ? currentEvent.sport_category : '')
    ).toLowerCase();

    const isBadminton = catSlug.includes('badminton');

    const fetchStandings = async () => {
      try {
        const rawSt = isBadminton
          ? await badmintonService.getStandings(currentEvent.slug)
          : await padelService.getStandings(currentEvent.slug);

        if (Array.isArray(rawSt) && rawSt.length > 0) {
          const formatted: KlasemenStandingItem[] = rawSt.map((item: any, idx: number) => ({
            position: item.position || idx + 1,
            playerName: item.playerName || item.player_name || `Pemain ${idx + 1}`,
            avatarUrl: item.avatarUrl || item.avatar_url,
            matches: item.gamesPlayed || item.matches || 0,
            wins: item.wins || 0,
            points: item.pointsScored || item.points || 0,
            winRate:
              item.winRate ??
              (item.gamesPlayed ? Math.round(((item.wins || 0) / item.gamesPlayed) * 100) : 0),
            isCurrentUser: (item.playerName || '').toLowerCase() === userDisplayName.toLowerCase(),
          }));
          setStandings(formatted);

          const foundUser = formatted.find(
            (f) => f.isCurrentUser || f.playerName.toLowerCase() === userDisplayName.toLowerCase()
          );
          if (foundUser) {
            setUserRank(foundUser.position);
          }
        }
      } catch (err) {
        console.error('Failed to load standings:', err);
      }
    };

    fetchStandings();
  }, [currentEvent, isRacket, userDisplayName]);

  const calculatedWinRate =
    eventStats.totalGames > 0
      ? Math.round((eventStats.wins / eventStats.totalGames) * 100)
      : 0;

  const activeCatalog = isRacket ? RACKET_METRIC_CATALOG : INVASION_METRIC_CATALOG;
  const activeStatKeys = exportStatKeys.length === 4
    ? exportStatKeys
    : (isRacket ? ['poin', 'point_loss', 'total_match', 'winrate'] : ['gol', 'assist', 'key_pass', 'clearance']);

  const statItems = activeStatKeys.map((key) => {
    const itemDef = findMetricFromCatalog(activeCatalog, key);
    return {
      key: itemDef.key,
      label: itemDef.label,
      icon: itemDef.icon,
      iconBg: itemDef.iconBg,
      iconColor: itemDef.iconColor,
      value: itemDef.getValue(eventStats, calculatedWinRate),
    };
  });



  const renderStatistikBlob = async (): Promise<{ blob: Blob; filename: string; eventName: string }> => {
    const slug = currentEvent?.slug || 'mabar-event';
    const eventName = currentEvent?.name || 'Mabar Event';
    const eventDate = currentEvent?.event_date || '';
    const sportCategory = currentEvent?.sport_category?.name || (isRacket ? 'Padel' : 'Futsal');
    const location = currentEvent?.lokasi_arena || currentEvent?.city || '';

    const blob = await mabarService.renderShareRecapV2(slug, {
      eventName,
      eventDateFormatted: formatDateDDMMYYYY(eventDate),
      scoreText: 'W',
      winnerName: userDisplayName,
      stats: {
        goals: eventStats.goals,
        assists: eventStats.assists,
        matches: eventStats.totalGames,
        win_rate: calculatedWinRate,
        wins: eventStats.wins,
        point: eventStats.point,
        point_loss: eventStats.pointLoss,
        is_racket: isRacket,
        rank: userRank,
        clean_sheet: eventStats.cleansheets,
        cleansheet: eventStats.cleansheets,
        cleansheets: eventStats.cleansheets,
        key_passes: eventStats.keyPasses,
        saves: eventStats.saves,
        clearances: eventStats.clearances,
        fouls: eventStats.fouls,
        motm: eventStats.motm,
        freestyle: eventStats.freestyle,
        winstreak: eventStats.winstreak,
        stat_keys: activeStatKeys,
      },
      playerName: userDisplayName,
      playerAvatarUrl: userAvatar,
      template: selectedTemplate,
      sportCategory,
      location,
      deltaMMR: eventDeltaMMR,
      standingsJson: standings.length > 0 ? JSON.stringify(standings) : undefined,
    });

    return {
      blob,
      filename: `recap-stats-${selectedTemplate}-${slug}.png`,
      eventName,
    };
  };

  const handleDownloadStatistik = async () => {
    try {
      setDownloading(true);
      const { blob, filename, eventName } = await renderStatistikBlob();
      const result = await downloadOrShareBlob(blob, filename, { title: eventName });
      if (result === 'saved') {
        showToast.success('Gambar statistik berhasil diunduh/disimpan ke Galeri');
      }
      setIsShareOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Gagal mengunduh gambar statistik';
      showToast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleShareStatistik = async () => {
    try {
      setSharing(true);
      const { blob, filename, eventName } = await renderStatistikBlob();
      const result = await shareBlob(blob, filename, {
        title: eventName,
        dialogTitle: 'Bagikan gambar',
      });
      if (result === 'shared') {
        showToast.success('Gambar statistik berhasil dibagikan');
        setIsShareOpen(false);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Gagal membagikan gambar statistik';
      showToast.error(message);
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    if (buttonOnly) {
      return <div className="h-12 bg-gray-100 rounded-2xl animate-pulse w-full" />;
    }
    return (
      <div className="bg-white rounded-3xl border border-[#E0E6ED] p-6 flex flex-col gap-4 animate-pulse shadow-xs">
        <div className="h-5 w-40 bg-gray-200 rounded-md" />
        <div className="h-20 bg-gray-100 rounded-2xl" />
        <div className="grid grid-cols-2 gap-2">
          <div className="h-14 bg-gray-100 rounded-xl" />
          <div className="h-14 bg-gray-100 rounded-xl" />
          <div className="h-14 bg-gray-100 rounded-xl" />
          <div className="h-14 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!currentEvent) {
    return null;
  }

  const eventTitle = currentEvent.name || 'Mabar Event';
  const rawDate = currentEvent.event_date || '';
  const formattedEventDate = formatDateDDMMYYYY(rawDate);
  const eventDateLoc = `${formattedEventDate}${
    currentEvent.lokasi_arena || currentEvent.city
      ? ` · ${currentEvent.lokasi_arena || currentEvent.city}`
      : ''
  }`;

  const availableTemplates = isRacket ? RACKET_TEMPLATES : INVASION_TEMPLATES;

  const shareModalJSX = isShareOpen && (
    <div
      className="fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end justify-center animate-in fade-in duration-200"
      style={{ marginTop: 0, paddingTop: 'calc(1rem * calc(1 - var(--tw-space-y-reverse, 0)))' }}
      onClick={() => setIsShareOpen(false)}
    >
      <div
        className="w-full max-w-md bg-white rounded-t-3xl p-5 pb-6 flex flex-col gap-4 shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="w-12 h-1.5 rounded-full bg-gray-300 self-center shrink-0" />

        {/* Header info */}
        <div className="flex flex-col gap-0.5">
          <h4 className="text-lg font-bold text-gray-900">Bagikan statistik</h4>
          <span className="text-xs text-gray-500 truncate">
            {eventTitle} · {formattedEventDate}
          </span>
        </div>

        {/* Live Preview Container (540x960 scaled down to 270x480) displaying sample background photo */}
        <div className="w-[270px] h-[480px] rounded-2xl overflow-hidden shadow-lg border border-gray-200 relative shrink-0 mx-auto bg-[#0E1726] flex items-center justify-center">
          <div
            className="w-[540px] h-[960px] absolute top-0 left-0 origin-top-left"
            style={{ transform: 'scale(0.5)' }}
          >
            <RecapCardRenderer
              template={selectedTemplate}
              eventName={eventTitle}
              eventDateFormatted={formattedEventDate}
              playerName={userDisplayName}
              playerAvatarUrl={userAvatar}
              rank={userRank}
              stats={{
                goals: eventStats.goals,
                assists: eventStats.assists,
                matches: eventStats.totalGames,
                wins: eventStats.wins,
                win_rate: calculatedWinRate,
                point: eventStats.point,
                point_loss: eventStats.pointLoss,
                is_racket: isRacket,
                rank: userRank,
                cleansheet: eventStats.cleansheets,
                key_passes: eventStats.keyPasses,
                saves: eventStats.saves,
                clearances: eventStats.clearances,
                fouls: eventStats.fouls,
                motm: eventStats.motm,
                freestyle: eventStats.freestyle,
                winstreak: eventStats.winstreak,
                selectedStatKeys: activeStatKeys,
              }}
              standings={standings}
              totalPlayers={standings.length || undefined}
              sportCategory={currentEvent.sport_category?.name || (isRacket ? 'Padel' : 'Futsal')}
              location={currentEvent.lokasi_arena || currentEvent.city || ''}
              deltaMMR={eventDeltaMMR}
              transparent={false}
            />
          </div>
        </div>

        {/* Template Selector Grid */}
        <div
          className={`grid gap-2.5 pt-1 ${
            availableTemplates.length === 2 ? 'grid-cols-2' : 'grid-cols-3'
          }`}
        >
          {availableTemplates.map((tmpl) => {
            const isActive = selectedTemplate === tmpl.id;
            return (
              <button
                key={tmpl.id}
                type="button"
                onClick={() => setSelectedTemplate(tmpl.id)}
                className={`flex flex-col items-center justify-between p-3 rounded-2xl text-center transition-all cursor-pointer min-h-[96px] ${
                  isActive
                    ? 'border-2 border-[#FC480D] bg-[#FFF4EE]'
                    : 'border border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <Icon
                  icon={tmpl.icon}
                  className={`w-6 h-6 mb-1 shrink-0 ${
                    isActive ? 'text-[#FC480D]' : 'text-gray-700'
                  }`}
                />
                <div className="flex flex-col gap-0.5 min-w-0 w-full">
                  <span
                    className={`text-xs truncate block ${
                      isActive ? 'font-bold text-[#FC480D]' : 'font-semibold text-gray-900'
                    }`}
                  >
                    {tmpl.title}
                  </span>
                  <span className="text-[9px] text-gray-400 leading-tight line-clamp-2 block font-normal">
                    {tmpl.desc}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Main Action Buttons */}
        <StatistikExportActions
          busy={downloading ? 'download' : sharing ? 'share' : null}
          onDownload={() => void handleDownloadStatistik()}
          onShare={() => void handleShareStatistik()}
          onClose={() => setIsShareOpen(false)}
        />
      </div>
    </div>
  );

  if (buttonOnly) {
    return (
      <>
        <button
          type="button"
          onClick={() => setIsShareOpen(true)}
          className="h-12 rounded-2xl bg-[#FC480D] hover:bg-[#D83B08] text-white text-sm font-bold flex items-center justify-center gap-2 w-full active:scale-98 transition-all shadow-sm cursor-pointer"
        >
          <Icon icon="ph:download-simple-bold" className="w-5 h-5" />
          <span>Download Statistik</span>
        </button>
        {shareModalJSX}
      </>
    );
  }

  return (
    <>
      <div className="bg-white rounded-3xl border border-[#E0E6ED] overflow-hidden flex flex-col shadow-xs">
        {/* Dark Header */}
        <div className="p-4 bg-[#0E1726] text-white flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              <span className="text-xs font-medium text-white/60">Rapor event kamu</span>
              <h3 className="text-base font-bold text-white truncate leading-snug">
                {eventTitle}
              </h3>
              <span className="text-xs text-white/60 truncate">
                {eventDateLoc}
              </span>

              {/* Kelola statistik button */}
              <button
                type="button"
                onClick={() => {
                  if (currentEvent?.slug) {
                    navigate(`/event/${currentEvent.slug}/manage-share`);
                  }
                }}
                className="mt-1.5 self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 text-white text-xs font-semibold border border-white/15 transition-all cursor-pointer shadow-xs"
              >
                <Icon icon="ph:sliders-horizontal-bold" className="w-3.5 h-3.5 text-white" />
                <span>Kelola statistik</span>
              </button>
            </div>

            {/* MMR Badge */}
            {eventDeltaMMR !== null && (
              <div
                className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5 shrink-0 ${
                  eventDeltaMMR > 0
                    ? 'bg-[#062c1d] border border-emerald-500/40 text-emerald-400'
                    : eventDeltaMMR < 0
                    ? 'bg-[#3b0c14] border border-rose-500/40 text-rose-400'
                    : 'bg-slate-800 border border-slate-700 text-slate-300'
                }`}
              >
                <Icon
                  icon={
                    eventDeltaMMR > 0
                      ? 'ph:trend-up-bold'
                      : eventDeltaMMR < 0
                      ? 'ph:trend-down-bold'
                      : 'ph:equals-bold'
                  }
                  className="w-3.5 h-3.5"
                />
                <span>
                  {eventDeltaMMR > 0
                    ? `+${Math.round(eventDeltaMMR)}`
                    : Math.round(eventDeltaMMR)}{' '}
                  MMR
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-3 border-t border-white/12 text-xs text-white/85">
            <Icon icon="ph:medal-fill" className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <span>Statistik kamu di event ini — layak dipamerkan.</span>
          </div>
        </div>

        {/* 4 Stat Boxes Grid (2x2) */}
        <div className="grid grid-cols-2 gap-px bg-[#E0E6ED] border-t border-[#E0E6ED]">
          {statItems.map((st) => (
            <div
              key={st.key}
              className="bg-white p-3.5 flex items-center gap-3"
            >
              <div
                className={`w-9 h-9 rounded-xl ${st.iconBg} ${st.iconColor} flex items-center justify-center flex-shrink-0`}
              >
                <Icon icon={st.icon} className="w-4.5 h-4.5" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-lg font-bold text-gray-900 leading-tight">
                  {st.value}
                </span>
                <span className="text-[11px] text-gray-500 font-medium truncate">
                  {st.label}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Action Button */}
        <div className="p-4 bg-white border-t border-[#E0E6ED]">
          <button
            type="button"
            onClick={() => setIsShareOpen(true)}
            className="h-11 rounded-full bg-[#FC480D] text-white text-sm font-semibold flex items-center justify-center gap-2 w-full hover:bg-[#D83B08] active:scale-98 transition-all shadow-sm cursor-pointer"
          >
            <Icon icon="ph:share-network" className="w-4.5 h-4.5" />
            <span>Bagikan</span>
          </button>
        </div>
      </div>

      {shareModalJSX}
    </>
  );
};

export default RaporEventCard;
